// Wire types for the white-glove onboarding admin API
// (/admin/onboarding, onboarding_operator gate).
//
// Nested run types mirror the Pydantic models; intake classification and envelopes
// are generated by scripts/gen_onboarding_intake_types.py. Other types in
// routes/onboarding_admin.py remain outside the codegen pipeline. The
// step/status unions below MUST stay in step with onboarding_state.STEP_ORDER
// and the CHECK constraints in migration 116 — a value the server can emit but
// the UI cannot name renders as a blank pill rather than failing loudly.

export type OnboardingStepName =
  | 'preflight'
  | 'allowlist'
  | 'provision'
  | 'team'
  | 'credits'
  | 'org'
  | 'email'

/**
 * Execution order. Index is also the predecessor relation.
 *
 * Declared as a typed readonly array rather than `as const` + indexed access:
 * the console forbids `as`, and spelling the union out separately means a new
 * step has to be added in both places, which is a feature here — the union is
 * the contract with the server's STEP_ORDER.
 */
export const ONBOARDING_STEPS: readonly OnboardingStepName[] = [
  'preflight',
  'allowlist',
  'provision',
  'team',
  'credits',
  'org',
  'email',
]

/**
 * Steps whose effect is irreversible or leaves the building.
 *
 * The UI requires a click for each of these, but that is a courtesy — the
 * server enforces ordering and single-execution in the step claim, so a
 * hand-rolled request cannot skip ahead.
 */
export const ONBOARDING_GATED_STEPS: readonly OnboardingStepName[] = [
  'provision',
  'team',
  'credits',
  'org',
  'email',
]

/**
 * `delivery_unknown` is terminal: the effect may or may not have landed, so it
 * is settled against its own evidence rather than retried.
 */
export type OnboardingStepStatus =
  | 'pending'
  | 'claimed'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'skipped'
  | 'delivery_unknown'

export const ONBOARDING_STEP_STATUSES: readonly OnboardingStepStatus[] = [
  'pending',
  'claimed',
  'running',
  'succeeded',
  'failed',
  'skipped',
  'delivery_unknown',
]

export interface OnboardingStep {
  step: OnboardingStepName
  status: OnboardingStepStatus
  gated: boolean
  error_code: string | null
  error_message: string | null
  /** Spark transaction id, SMTP Message-ID, or GoTrue user id. */
  effect_ref: string | null
  amount: number | null
  /** ISO timestamps; drive the "still running" / "interrupted" display. */
  claimed_at?: string | null
  heartbeat_at?: string | null
}

export interface OnboardingTarget {
  target_id: string
  /** Null once the account has been purged; the row survives anonymised. */
  email: string | null
  /** For the bulk email confirmation's greeting preview; null once purged. */
  first_name?: string | null
  credits: number
  org_slug: string | null
  /** Pinned at the end of provision (or of the team step for a team run), not at run creation. */
  wallet_kind: 'personal' | 'team' | null
  /** Frozen credit wallet; never infer it from requested/created team_id. */
  credited_team_id?: string | null
  credit_wallet_name?: string | null
  credit_wallet_balance?: number | null
  steps: OnboardingStep[]
  /** Absent from a pre-team-step server, meaning no team. */
  team_mode?: OnboardingTeamMode | 'group_member' | null
  team_owner_target_id?: string | null
  team_dependency_ready?: boolean
  /** The name to create, or the joined team's current name. */
  team_name?: string | null
  /** The team joined, or the one the team step created (null until it has). */
  team_id?: string | null
  team_role?: OnboardingTeamRole | null
  credit_billing_mode?: 'prepaid' | 'postpaid'
  billing_period_anchor?: string | null
  billing_period_months?: 1 | 3 | 6 | 12 | null
  billing_timezone?: string | null
  billing_contract_id?: string | null
}

/** `create` = a new shared team the partner owns; `join` = an existing team. */
export type OnboardingTeamMode = 'personal' | 'create' | 'join' | 'configure'
export const ONBOARDING_TEAM_MODES: readonly OnboardingTeamMode[] = [
  'personal', 'create', 'join', 'configure',
]

/** The role a joining partner gets. Never owner — that is what `create` is for. */
export type OnboardingTeamRole = 'member' | 'admin'
export const ONBOARDING_TEAM_ROLES: readonly OnboardingTeamRole[] = ['member', 'admin']

/** A team the looked-up account owns or administers: a valid `join` target. */
export interface OnboardingTeamOption {
  team_id: string
  name: string
  kind: 'personal' | 'team'
  role: 'owner' | 'admin'
  member_count: number
  member_limit: number
  credit_billing_mode?: 'prepaid' | 'postpaid'
  billing_period_anchor?: string | null
  billing_period_months?: 1 | 3 | 6 | 12 | null
  billing_timezone?: string | null
  billing_contract_id?: string | null
}

export interface OnboardingTeamLookup {
  email: string
  user_found: boolean
  teams: OnboardingTeamOption[]
}

export interface OnboardingRun {
  scope?: 'full' | 'targets'
  scope_target_ids?: string[] | null
  run_id: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  reason: string
  actor_email: string
  targets: OnboardingTarget[]
  /** The run may re-onboard addresses that already have a run. */
  allow_repeat_recipients?: boolean
  /** Server-side test-send attestation; `email_test_current` is false for an older template. */
  email_test_sent_at?: string | null
  email_test_current?: boolean
  /** Which email the run sends (P3); absent from a pre-P3 server, meaning default. */
  email_template_kind?: OnboardingEmailTemplateKind
  /** The per-run aggregate credit ceiling in force (override, else the env default). */
  credit_ceiling?: number
  credit_ceiling_override?: number | null
  /** Computed on read from step statuses; never stored. */
  progress?: OnboardingRunProgress | null
}

/** Per-step bucket counts. `blocked` is derived in the console from preflight. */
export interface OnboardingStepProgress {
  succeeded: number
  skipped: number
  failed: number
  delivery_unknown: number
  claimed: number
  pending: number
}

export interface OnboardingRunProgress {
  status: 'in_progress' | 'needs_attention' | 'completed' | 'cancelled'
  completed_targets: number
  attention_targets: number
  steps: Partial<Record<OnboardingStepName, OnboardingStepProgress>>
}

/** A row exactly as the server parsed it from the CSV. */
export interface OnboardingCsvRow {
  classification?: OnboardingIntakeClassification
  history?: OnboardingRecipientHistory | null
  record: number
  lines: number[]
  email: string
  first_name: string | null
  credits: number
  org_slug: string | null
  org_display_name: string | null
  credit_billing_mode?: 'prepaid' | 'postpaid'
  billing_period_anchor?: string | null
  billing_period_months?: 1 | 3 | 6 | 12 | null
  billing_timezone?: string | null
}

/** One CSV problem. `record` is null for file-, header- and run-level problems. */
export interface OnboardingCsvError {
  record: number | null
  lines: number[] | null
  column: string | null
  message: string
  /** Set for cross-run conflicts, so the re-onboard override can name its rows. */
  conflict_kind: OnboardingRecipientConflict['kind'] | null
}

export interface OnboardingCsvResult {
  scopes?: OnboardingRunScope[]
  rows: OnboardingCsvRow[]
  total_credits: number
  credit_ceiling: number
  /** Present only when the run was created (not for validate_only). */
  run: OnboardingRun | null
}

/** The 422 CSV_INVALID body: every problem at once, and nothing was created. */
export interface OnboardingCsvInvalid {
  errors: OnboardingCsvError[]
  rows: OnboardingCsvRow[]
  total_credits: number | null
  credit_ceiling: number | null
}

export interface OnboardingCsvRequest {
  reason: string
  csvText: string
  validateOnly: boolean
  acknowledgedPriorTargets?: Record<string, string>
  allowRepeatRecipients: boolean
  creditCeilingOverride: number | null
}

/** One reason an address may not be added to a new run (409 RECIPIENT_CONFLICT). */
export interface OnboardingRecipientConflict {
  email: string
  run_id: string
  kind: 'in_flight' | 'already_onboarded' | 'pending_elsewhere'
}

export interface OnboardingRunTargetInput {
  email: string
  first_name?: string | null
  credits?: number
  org_slug?: string | null
  org_display_name?: string | null
  /** Omit (or null) for no team. The server refuses fields the mode does not use. */
  team_mode?: OnboardingTeamMode | null
  team_name?: string | null
  join_team_id?: string | null
  team_role?: OnboardingTeamRole | null
  credit_billing_mode?: 'prepaid' | 'postpaid'
  billing_period_anchor?: string | null
  billing_period_months?: 1 | 3 | 6 | 12 | null
  billing_timezone?: string | null
}

export interface OnboardingTargetPreflight {
  target_id: string
  email: string
  registration_allowlisted: boolean
  auth_allowlist_enforced: boolean
  auth_allowlist_satisfied: boolean
  account_exists: boolean
  account_confirmed: boolean
  current_balance: number | null
  current_org_slug: string | null
  /**
   * Non-null means this environment cannot complete the onboarding — the run
   * must not be started here. Shown verbatim; it names the required fix.
   */
  blocking_reason: string | null
}

export interface OnboardingPreflight {
  /** False means the registration allowlist enforces nothing. Hard stop. */
  trigger_enforcing: boolean
  trigger_blocking_reason: string | null
  targets: OnboardingTargetPreflight[]
}

export interface OnboardingAdvanceOptions {
  /** Admits every FUTURE signup from the domain — deliberately opt-in. */
  allowWholeDomain?: boolean
  /** Re-attributing another customer's analytics — deliberately opt-in. */
  allowOrgMove?: boolean
  /** The source org the operator saw when approving a move; the server refuses if it changed. */
  expectedCurrentOrg?: string | null
}

/**
 * The welcome email as the partner would receive it, with a placeholder where
 * the password goes. Reading this changes nothing on the server.
 */
export interface OnboardingEmailPreview {
  subject: string
  /** Render ONLY inside a script-free sandboxed iframe. */
  html: string
  text: string
  /** Problems that would make the real send fail, e.g. SMTP not configured. */
  warnings: string[]
}

export type OnboardingEmailTemplateKind = 'default' | 'custom'

/** What this environment allows. Answers even with the tool switched off. */
export interface OnboardingCapabilities {
  onboarding_enabled: boolean
  custom_templates_enabled: boolean
}

/**
 * The run's welcome-email SOURCE, as the editor edits it — the custom version,
 * or the shipped default to start from. The HTML references the logo by
 * `cid:`; only previews swap it for a data URI.
 */
export interface OnboardingEmailTemplate {
  kind: OnboardingEmailTemplateKind
  subject: string
  html: string
  text: string
  template_sha: string
  /** Whether a save or reset would be accepted right now. */
  editable: boolean
  /** Why the email can no longer change (an email was attempted, or the run is over). */
  frozen_reason: string | null
  custom_templates_enabled: boolean
  updated_by: string | null
  updated_at: string | null
}

export interface OnboardingEmailTemplateDraft {
  subject: string
  html: string
  text: string
}

/** One problem found by the server's template policy. */
export interface OnboardingTemplateIssue {
  code: string
  message: string
  field: string | null
  line: number | null
}

/** A lint plus a render of an unsaved draft. Non-empty `errors` means it cannot be saved. */
export interface OnboardingEmailTemplatePreview {
  errors: OnboardingTemplateIssue[]
  warnings: OnboardingTemplateIssue[]
  subject: string | null
  /** Render ONLY inside a script-free sandboxed iframe. */
  html: string | null
  text: string | null
}

/** Where an operator test send went — always the caller, never the partner. */
export interface OnboardingTestSendResult {
  sent_to: string
  message_id: string
}

export interface OnboardingOrgSummary {
  slug: string
  display_name: string
  members: number
}

export interface OnboardingOrgRoster {
  slug: string
  members: string[]
}

/** The only two outcomes an operator may assert for a delivery_unknown send. */
export type OnboardingSettleOutcome = 'delivered' | 'not_delivered'

export const ONBOARDING_SETTLE_OUTCOMES: readonly OnboardingSettleOutcome[] = [
  'delivered',
  'not_delivered',
]

/** True for a single-target advance response; false for a whole run (old server, mid-rollout). */
export function isTargetResponse(
  value: OnboardingTarget | OnboardingRun,
): value is OnboardingTarget {
  return !('run_id' in value) && 'target_id' in value
}

/** Replace one target inside a run, leaving every other target untouched. */
export function withTarget(run: OnboardingRun, target: OnboardingTarget): OnboardingRun {
  return {
    ...run,
    targets: run.targets.map((t) => (t.target_id === target.target_id ? target : t)),
  }
}

export interface OnboardingRunScope { run_id: string; target_ids: string[] }
export type OnboardingIntakeClassification = import('./generated/onboardingIntake').OnboardingIntakeClassification
export interface OnboardingHistoryEntry {
  run_id: string
  target_id: string
  run_status: string
  actor_email: string
  created_at: string
  completed_at: string | null
  complete: boolean
  grant_landed: boolean
  steps: { step: OnboardingStepName; status: OnboardingStepStatus; claimed_by: string | null; finished_at: string | null; effect_ref: string | null }[]
}
export interface OnboardingRecipientHistory {
  email: string
  classification: OnboardingIntakeClassification
  account_exists: boolean
  history: OnboardingHistoryEntry[]
  selected: OnboardingHistoryEntry | null
  next_step: OnboardingStepName | null
  reason: string | null
}

/** Stable scope for requests and caches. Empty input must never widen to full. */
export function normalizeOnboardingScope(targetIds?: string[]): string[] | undefined {
  if (targetIds === undefined) return undefined
  if (targetIds.length === 0) throw new Error('Onboarding target scope must not be empty')
  return [...new Set(targetIds.map((id) => id.toLowerCase()))].sort()
}

export function onboardingScopeQuery(targetIds?: string[], responseTarget = false): string {
  const query = new URLSearchParams()
  if (responseTarget) query.set('response', 'target')
  for (const id of normalizeOnboardingScope(targetIds) ?? []) query.append('target_ids', id)
  const value = query.toString()
  return value ? `?${value}` : ''
}

/** Refuse a full/incorrect response before it can enter a scoped query cache. */
export function validateScopedOnboardingRun(run: OnboardingRun, targetIds?: string[]): OnboardingRun {
  const ids = normalizeOnboardingScope(targetIds)
  if (ids === undefined) return run
  if (run.scope !== 'targets' || JSON.stringify(normalizeOnboardingScope(run.scope_target_ids ?? [])) !== JSON.stringify(ids)
    || JSON.stringify(normalizeOnboardingScope(run.targets.map((target) => target.target_id))) !== JSON.stringify(ids)) {
    throw new Error('Server returned an invalid onboarding target scope')
  }
  return run
}

/** JSON intake preserves team configuration and creates one new run. */
export interface OnboardingIntakeRequest {
  reason: string
  targets: OnboardingRunTargetInput[]
  validateOnly: boolean
  sharedTeam: boolean
  acknowledgedPriorTargets: Record<string, string>
}
export type OnboardingIntakeResult = import('./generated/onboardingIntake').IntakeView
